import type { RootStore } from '../stores/root';
import { shouldContinueLoginInApp } from '../stores/constants';
import {
  SEARCH_PARAMS,
  WIX_DEFAULT_PARENT_ACCOUNT_ID,
  WIX_PARENT_ACCOUNT_HINT,
} from './constants';

type WithParent = { parentAccountId?: string | null };

/**
 * Which of an email's several accounts the login asks for: the one in the
 * URL's organization (picked in the account selector, or the host's), else
 * the older `?parentHint=` that other apps link with (`wix` for an account
 * directly under Wix), also when no account is in that organization.
 * Undefined when neither asks for one of them, so the selector asks again. A
 * parentHint that matches no account falls back to the first one, as it
 * always has.
 */
export const findRequestedAccount = <T extends WithParent>(
  accounts: T[],
  {
    organizationId,
    parentHint,
  }: { organizationId?: string; parentHint?: string },
): T | undefined => {
  const inOrganization =
    organizationId &&
    accounts.find(
      (account) =>
        (account.parentAccountId || WIX_DEFAULT_PARENT_ACCOUNT_ID) ===
        organizationId,
    );
  if (inOrganization) {
    return inOrganization;
  }
  if (parentHint) {
    return (
      accounts.find(
        (account) =>
          (!account.parentAccountId &&
            parentHint === WIX_PARENT_ACCOUNT_HINT) ||
          account.parentAccountId === parentHint,
      ) ?? accounts[0]
    );
  }
  return undefined;
};

/**
 * The URL's organizationId, for the account lookups: the account picked in
 * the account selector, or a host's. Read only with the in-app path on
 * (shouldContinueLoginInApp); otherwise the lookups narrow by parentHint
 * alone, as before.
 */
export const getRequestedOrganizationId = ({
  experiments,
  navigationStore,
}: Pick<RootStore, 'experiments' | 'navigationStore'>): string | undefined =>
  (shouldContinueLoginInApp(experiments) &&
    navigationStore.getQueryParam(SEARCH_PARAMS.ORGANIZATION_ID)) ||
  undefined;
